/**
 * Aggregates all DSP plots into a whole-collection detection summary for the
 * exported report.
 *
 * ## Why this isn't `collectionHexagonsSummary`
 *
 * The hexagon summary returns a `{ stats, detections }` pair with the same shape
 * as a single cell, so one component can render either. It gets there by summing
 * cell areas into a total ground area and running `deriveCellStats` over it,
 * which is valid because hexagon cells tile the ground without overlapping.
 *
 * Drone image footprints *do* overlap, so that step would be wrong here twice
 * over: the summed area double-counts ground (`/detected-area` reports the union
 * as `covered_area_m2`), and a density derived from it would be understated. The
 * endpoint also keeps duplicate detections on purpose — a tree photographed by
 * two overlapping images is a real observation in both plots — so the summed
 * count is a per-plot tally, not a survey total.
 *
 * This module therefore reports only what is defensible without the union area:
 * per-object counts/areas/heights and the plot tally. The summed footprint area
 * is exposed under an explicitly-named field so no caller can mistake it for the
 * surveyed area.
 *
 * @module collectionDspSummary
 */

/**
 * @param {import('./collectionDspToPlots').DspPlot[]} plots
 * @returns {{
 *   detections: Object,
 *   stats: { count: number, area: number, mean_height: number|null },
 *   plotCount: number,
 *   totalFootprintArea: number,
 *   totalObjectCount: number
 * } | null} null when there are no plots.
 *
 * `detections` is keyed by object and shaped like a plot's own entry
 * (`{ count, mean_height, sum_object_area }`) so the report's per-class table and
 * the details panel read it the same way. `totalFootprintArea` is Σ plot
 * footprints — overlapping ground counted once per image, NOT the surveyed area.
 * `totalObjectCount` is Σ per-plot counts, which exceeds the collection's
 * de-duplicated object count by the image overlap.
 *
 * `stats` mirrors the three fields a plot's own `stats` exposes to the details
 * panel (count, area, mean height) so one presentational component renders
 * either. It is built explicitly rather than by running `deriveCellStats` over
 * the totals — that would also produce a density and hectare equivalent, and
 * those are exactly the figures the overlapping footprint sum makes wrong.
 */
export const collectionDspSummary = plots => {
	if (!plots?.length) return null;

	// Per-object accumulators. Height is count-weighted so a plot holding one
	// tree doesn't pull the mean as hard as a plot holding fifty.
	const acc = {};
	let totalFootprintArea = 0;
	let totalObjectCount = 0;

	for (const plot of plots) {
		totalFootprintArea += plot.stats?.area ?? 0;

		for (const [objectKey, det] of Object.entries(plot.detections ?? {})) {
			const entry = (acc[objectKey] ??= {
				count: 0,
				sum_object_area: 0,
				heightWeighted: 0,
				heightWeight: 0,
			});

			const count = det?.count ?? 0;
			entry.count += count;
			totalObjectCount += count;
			entry.sum_object_area += det?.sum_object_area ?? 0;

			// A plot with none of an object reports null means, not 0 — an
			// undefined mean carries no signal and must not be averaged in.
			if (det?.mean_height != null && count > 0) {
				entry.heightWeighted += det.mean_height * count;
				entry.heightWeight += count;
			}
		}
	}

	const detections = {};
	let heightWeighted = 0;
	let heightWeight = 0;
	for (const [objectKey, entry] of Object.entries(acc)) {
		const meanHeight =
			entry.heightWeight > 0 ? entry.heightWeighted / entry.heightWeight : null;
		detections[objectKey] = {
			count: entry.count,
			mean_height: meanHeight,
			sum_object_area: entry.sum_object_area,
		};
		// Roll the per-object weighted sums up one more level for the overall mean.
		heightWeighted += entry.heightWeighted;
		heightWeight += entry.heightWeight;
	}

	return {
		detections,
		stats: {
			count: totalObjectCount,
			area: totalFootprintArea,
			mean_height: heightWeight > 0 ? heightWeighted / heightWeight : null,
		},
		plotCount: plots.length,
		totalFootprintArea,
		totalObjectCount,
	};
};

export default collectionDspSummary;
